<template>
  <div class="ai-assistant-wrapper">
    <!-- 悬浮助手按钮 -->
    <div
      ref="floatBtnRef"
      class="ai-float-btn"
      :class="{
        'is-dragging': isDragging,
        'is-collapsed': isCollapsed,
        'is-open': dialogVisible,
      }"
      :style="floatStyle"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="handleFloatClick"
    >
      <div class="float-inner">
        <el-icon :size="26"><ChatDotRound /></el-icon>
        <span class="float-label">AI助手</span>
      </div>
    </div>

    <!-- 全屏对话框 -->
    <el-dialog
      v-model="dialogVisible"
      class="ai-assistant-dialog qwen-style"
      width="96vw"
      :show-close="false"
      :close-on-click-modal="false"
      :modal="true"
      append-to-body
      destroy-on-close
      align-center
    >
      <div class="qwen-layout">
        <!-- 左侧边栏 -->
        <aside class="qwen-sidebar">
          <div class="sidebar-header">
            <button class="new-chat-btn" @click="createNewSession">
              <el-icon><Plus /></el-icon>
              <span>新建对话</span>
            </button>
          </div>

          <div class="sidebar-body">
            <div class="sidebar-section-title">最近对话</div>
            <div class="session-list" v-loading="sessionsLoading">
              <div
                v-for="session in sessions"
                :key="session.id"
                class="session-item"
                :class="{ active: currentSession && currentSession.id === session.id }"
                @click="selectSession(session)"
              >
                <el-icon class="session-icon"><ChatLineRound /></el-icon>
                <span class="session-title">{{ session.title || '新对话' }}</span>
                <el-icon class="session-delete" @click.stop="deleteSession(session)"><Close /></el-icon>
              </div>
              <div v-if="sessions.length === 0 && !sessionsLoading" class="empty-sessions">
                暂无历史对话
              </div>
            </div>
          </div>
        </aside>

        <!-- 右侧主区域 -->
        <main class="qwen-main">
          <!-- 顶部模型选择 -->
          <header class="qwen-header">
            <div class="header-spacer"></div>
            <el-dropdown trigger="click" @command="handleModelChange" v-if="textModels.length > 0">
              <div class="model-selector">
                <span>{{ selectedModel?.name || '选择模型' }}</span>
                <el-icon class="model-arrow"><ArrowDown /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="model in textModels"
                    :key="model.id"
                    :command="model.id"
                    :class="{ active: selectedModelId === model.id }"
                  >
                    <div class="model-option">
                      <span>{{ model.name }}</span>
                      <el-tag v-if="model.is_multimodal" type="success" size="small">多模态</el-tag>
                    </div>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-button class="close-btn" text circle @click="dialogVisible = false">
              <el-icon><Close /></el-icon>
            </el-button>
          </header>

          <!-- 中间内容区 -->
          <div ref="messagesRef" class="qwen-content">
            <!-- 欢迎页 -->
            <div v-if="messages.length === 0" class="qwen-welcome">
              <div class="welcome-title">
                <span class="welcome-logo">🤖</span>
                你好，我是 AI 助手
              </div>
              <div class="welcome-tips" v-if="selectedModel && selectedModel.is_multimodal">
                当前模型支持多模态对话，可上传或粘贴图片、视频（视频 ≤12MB / 180秒）
              </div>
            </div>

            <!-- 消息列表 -->
            <template v-else>
              <div
                v-for="msg in messages"
                :key="msg.id"
                class="message-row"
                :class="msg.role"
              >
                <div class="message-bubble">
                  <div class="message-images" v-if="msg.images && msg.images.length > 0">
                    <el-image
                      v-for="(img, idx) in msg.images"
                      :key="'img'+idx"
                      :src="img"
                      :preview-src-list="msg.images"
                      :initial-index="idx"
                      fit="cover"
                      class="message-image"
                      hide-on-click-modal
                      preview-teleported
                    />
                  </div>
                  <div class="message-videos" v-if="msg.videos && msg.videos.length > 0">
                    <video
                      v-for="(v, idx) in msg.videos"
                      :key="'vid'+idx"
                      :src="v"
                      controls
                      preload="metadata"
                      class="message-video"
                    ></video>
                  </div>
                  <div class="message-text" v-html="renderMarkdown(msg.content)"></div>
                </div>
              </div>

              <div v-if="sending" class="message-row assistant">
                <div class="message-bubble">
                  <div class="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- 底部输入区 -->
          <footer class="qwen-footer">
            <div class="input-card">
              <!-- 快捷标签 -->
              <div class="quick-tags">
                <span class="quick-tag"><el-icon><Lightning /></el-icon> 快速</span>
                <span class="quick-tag" @click="openKnowledgePanel"><el-icon><Collection /></el-icon> 知识库</span>
                <span class="quick-tag"><el-icon><More /></el-icon> 更多</span>
              </div>

              <!-- 待上传图片 -->
              <div v-if="pendingImages.length > 0" class="pending-images">
                <div v-for="(img, idx) in pendingImages" :key="'pimg'+idx" class="pending-image-item">
                  <el-image :src="img" fit="cover" class="pending-image" :preview-src-list="pendingImages" :initial-index="idx" hide-on-click-modal preview-teleported />
                  <div class="pending-image-delete" @click="removePendingImage(idx, 'image')">
                    <el-icon><Close /></el-icon>
                  </div>
                </div>
              </div>

              <!-- 待上传视频 -->
              <div v-if="pendingVideos.length > 0" class="pending-images">
                <div v-for="(vid, idx) in pendingVideos" :key="'pvid'+idx" class="pending-image-item pending-video-item">
                  <video :src="vid.localUrl" class="pending-video" preload="metadata" muted></video>
                  <div class="pending-image-delete" @click="removePendingImage(idx, 'video')">
                    <el-icon><Close /></el-icon>
                  </div>
                </div>
              </div>

              <div class="input-line" :class="{ expanded: inputExpanded }">
                <el-dropdown
                  v-if="selectedModel && selectedModel.is_multimodal"
                  trigger="click"
                  placement="top"
                  :disabled="uploadingImages"
                  @command="triggerFilePick"
                >
                  <el-button class="attach-btn" text-circle :loading="uploadingImages">
                    <el-icon :size="20"><Plus /></el-icon>
                  </el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item command="image">上传图片</el-dropdown-item>
                      <el-dropdown-item command="video">上传视频（≤12MB/180秒）</el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
                <textarea
                  v-model="inputText"
                  class="qwen-input"
                  :rows="inputExpanded ? 8 : 2"
                  placeholder="向 AI 助手提问...（Enter 发送，Shift+Enter 换行）"
                  @keydown.enter.exact.prevent="sendMessage"
                  @input="autoGrow"
                  @paste="handlePaste"
                  ref="inputRef"
                ></textarea>
                <div class="input-side-btns">
                  <el-tooltip :content="inputExpanded ? '收起输入框' : '展开输入框'" placement="top">
                    <el-button class="expand-btn" text-circle @click="toggleInputExpand">
                      <el-icon :size="18">
                        <Fold v-if="inputExpanded" />
                        <FullScreen v-else />
                      </el-icon>
                    </el-button>
                  </el-tooltip>
                  <el-button
                    class="send-btn"
                    :class="{ active: canSend }"
                    circle
                    :loading="sending"
                    :disabled="!canSend"
                    @click="sendMessage"
                  >
                    <el-icon :size="18"><Top /></el-icon>
                  </el-button>
                </div>
              </div>
            </div>
          </footer>
        </main>
      </div>
    </el-dialog>

    <!-- 知识库选择弹窗 -->
    <el-dialog
      v-model="knowledgePanelVisible"
      title="选择知识库"
      width="480px"
      :close-on-click-modal="true"
      append-to-body
      destroy-on-close
      align-center
    >
      <div class="kb-panel">
        <el-input
          v-model="kbSearchKeyword"
          placeholder="搜索知识库..."
          :prefix-icon="Search"
          clearable
          class="kb-search"
        />
        <div class="kb-sort-hint">按更新时间排序</div>
        <div class="kb-list" v-loading="kbLoading">
          <div
            v-for="base in filteredBases"
            :key="base.id"
            class="kb-pick-item"
            @click="importKnowledge(base)"
          >
            <el-icon class="kb-pick-icon"><Collection /></el-icon>
            <div class="kb-pick-body">
              <div class="kb-pick-name">{{ base.name }}</div>
              <div class="kb-pick-desc">{{ base.description || '暂无简介' }}</div>
            </div>
            <div class="kb-pick-count">{{ base.knowledge_count || 0 }} 知识</div>
          </div>
          <el-empty v-if="filteredBases.length === 0 && !kbLoading" description="没有找到知识库" :image-size="60" />
        </div>
      </div>
    </el-dialog>

    <input
      type="file"
      ref="fileInput"
      style="display: none"
      :accept="filePickType === 'video' ? 'video/*' : 'image/*'"
      multiple
      @change="handleFileChange"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import api from '../api'

// 配置 marked
marked.setOptions({
  breaks: true,
  gfm: true
})

const dwConfirm = (options) => {
  if (window.$dwConfirm) return window.$dwConfirm(options)
  return Promise.reject(new Error('ConfirmDialog not ready'))
}

export default {
  name: 'AiAssistant',
  setup() {
    // 悬浮按钮
    const floatBtnRef = ref(null)
    const isDragging = ref(false)
    const isMouseDown = ref(false)
    const didDrag = ref(false)
    const isCollapsed = ref(false)
    const dragStart = ref({ x: 0, y: 0, time: 0, posX: 0, posY: 0 })
    const floatPosition = ref({ x: 24, y: 0 })
    const windowWidth = ref(window.innerWidth)
    const windowHeight = ref(window.innerHeight)
    const FLOAT_W = 80
    const FLOAT_H = 64
    const COLLAPSE_OFFSET = 46
    const DRAG_THRESHOLD = 5

    // 对话框
    const dialogVisible = ref(false)
    const currentSession = ref(null)
    const sessions = ref([])
    const sessionsLoading = ref(false)
    const messages = ref([])
    const messagesRef = ref(null)

    // 模型
    const textModels = ref([])
    const selectedModelId = ref(null)
    const selectedModel = computed(() => textModels.value.find(m => m.id === selectedModelId.value))

    // 输入
    const inputText = ref('')
    const inputRef = ref(null)
    const sending = ref(false)
    const inputExpanded = ref(false)

    // 输入框高度随内容自动增长（展开状态下放开通高，由 rows 控制基础高度）
    const autoGrow = () => {
      const el = inputRef.value
      if (!el || inputExpanded.value) return
      el.style.height = 'auto'
      // 收起状态最多长到 6 行，再高内部滚动
      const lineH = 24
      const maxH = lineH * 6 + 12
      el.style.height = Math.min(el.scrollHeight, maxH) + 'px'
    }

    const toggleInputExpand = () => {
      inputExpanded.value = !inputExpanded.value
      nextTick(() => {
        const el = inputRef.value
        if (!el) return
        if (inputExpanded.value) {
          el.style.height = 'auto'  // 交给 rows=8
        } else {
          autoGrow()
        }
        el.focus()
      })
    }

    const pendingImages = ref([])
    const pendingVideos = ref([])
    const uploadingImages = ref(false)
    const fileInput = ref(null)
    const filePickType = ref('image')

    const canSend = computed(() => {
      return selectedModelId.value && !sending.value
        && (inputText.value.trim() || pendingImages.value.length > 0 || pendingVideos.value.length > 0)
    })

    const initFloatPosition = () => {
      floatPosition.value = {
        x: 24,
        y: window.innerHeight - FLOAT_H - 28
      }
    }

    const floatStyle = computed(() => {
      let x = floatPosition.value.x
      let y = floatPosition.value.y
      if (x <= 8) x = -COLLAPSE_OFFSET
      if (x >= windowWidth.value - FLOAT_W - 8) x = windowWidth.value - FLOAT_W + COLLAPSE_OFFSET
      if (y <= 8) y = -COLLAPSE_OFFSET
      if (y >= windowHeight.value - FLOAT_H - 8) y = windowHeight.value - FLOAT_H + COLLAPSE_OFFSET
      return { left: x + 'px', top: y + 'px' }
    })

    const checkCollapsed = () => {
      const x = floatPosition.value.x
      const y = floatPosition.value.y
      isCollapsed.value = x <= 8 || x >= windowWidth.value - FLOAT_W - 8 || y <= 8 || y >= windowHeight.value - FLOAT_H - 8
    }

    // 拖拽逻辑：区分点击与拖拽
    const startDrag = (e) => {
      if (dialogVisible.value) return
      isMouseDown.value = true
      didDrag.value = false
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      dragStart.value = {
        x: clientX - floatPosition.value.x,
        y: clientY - floatPosition.value.y,
        time: Date.now(),
        posX: clientX,
        posY: clientY
      }
      document.addEventListener('mousemove', onDrag)
      document.addEventListener('mouseup', stopDrag)
      document.addEventListener('touchmove', onDrag, { passive: false })
      document.addEventListener('touchend', stopDrag)
    }

    const onDrag = (e) => {
      if (!isMouseDown.value) return
      e.preventDefault()
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY

      // 判断是否真正开始拖拽
      const dx = Math.abs(clientX - dragStart.value.posX)
      const dy = Math.abs(clientY - dragStart.value.posY)
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        isDragging.value = true
        didDrag.value = true
      }

      if (!isDragging.value) return

      let x = clientX - dragStart.value.x
      let y = clientY - dragStart.value.y
      x = Math.max(0, Math.min(windowWidth.value - FLOAT_W, x))
      y = Math.max(0, Math.min(windowHeight.value - FLOAT_H, y))
      floatPosition.value = { x, y }
      isCollapsed.value = false
    }

    const stopDrag = () => {
      isMouseDown.value = false
      isDragging.value = false
      document.removeEventListener('mousemove', onDrag)
      document.removeEventListener('mouseup', stopDrag)
      document.removeEventListener('touchmove', onDrag)
      document.removeEventListener('touchend', stopDrag)
      checkCollapsed()
    }

    const handleFloatClick = () => {
      // 如果发生过拖拽，不触发开关
      if (didDrag.value) {
        didDrag.value = false
        return
      }
      // 单击切换：打开 ↔ 关闭
      dialogVisible.value = !dialogVisible.value
      if (dialogVisible.value) {
        loadSessions()
        loadTextModels()
      }
    }

    const onResize = () => {
      windowWidth.value = window.innerWidth
      windowHeight.value = window.innerHeight
      floatPosition.value.x = Math.min(floatPosition.value.x, windowWidth.value - FLOAT_W)
      floatPosition.value.y = Math.min(floatPosition.value.y, windowHeight.value - FLOAT_H)
      checkCollapsed()
    }

    // API
    const loadTextModels = async () => {
      try {
        const res = await api.get('/models/?model_type=text')
        textModels.value = res.data.filter(m => m.is_enabled)
        // 默认选中排序第一个模型
        if (!selectedModelId.value && textModels.value.length > 0) {
          selectedModelId.value = textModels.value[0].id
        }
      } catch (e) {
        console.error('加载文本模型失败:', e)
      }
    }

    const loadSessions = async () => {
      sessionsLoading.value = true
      try {
        const res = await api.get('/assistant/sessions/')
        sessions.value = res.data
      } catch (e) {
        console.error('加载会话失败:', e)
      } finally {
        sessionsLoading.value = false
      }
    }

    const createNewSession = async () => {
      if (!selectedModelId.value) {
        if (textModels.value.length > 0) {
          selectedModelId.value = textModels.value[0].id
        } else {
          ElMessage.warning('请先添加并启用一个文本模型')
          return
        }
      }
      try {
        const res = await api.post('/assistant/sessions/', {
          model_id: selectedModelId.value,
          title: '新对话'
        })
        sessions.value.unshift(res.data)
        await selectSession(res.data)
      } catch (e) {
        ElMessage.error('创建会话失败')
      }
    }

    const selectSession = async (session) => {
      currentSession.value = session
      if (session.model) selectedModelId.value = session.model
      try {
        const res = await api.get(`/assistant/sessions/${session.id}/`)
        messages.value = res.data.messages || []
        currentSession.value = res.data
        if (res.data.model) selectedModelId.value = res.data.model
        nextTick(() => scrollToBottom())
      } catch (e) {
        console.error('加载消息失败:', e)
      }
    }

    const deleteSession = async (session) => {
      try {
        const confirmed = await dwConfirm({
          title: '删除对话',
          message: `确定删除「${session.title || '新对话'}」吗？删除后无法恢复。`,
          type: 'warning',
          confirmText: '删除',
          cancelText: '取消'
        })
        if (!confirmed) return
        await api.delete(`/assistant/sessions/${session.id}/`)
        sessions.value = sessions.value.filter(s => s.id !== session.id)
        if (currentSession.value && currentSession.value.id === session.id) {
          currentSession.value = null
          messages.value = []
        }
        ElMessage.success('已删除')
      } catch (e) {
        if (e !== 'cancel') console.error(e)
      }
    }

    const handleModelChange = async (modelId) => {
      selectedModelId.value = modelId
      if (currentSession.value && currentSession.value.model !== modelId) {
        try {
          await api.put(`/assistant/sessions/${currentSession.value.id}/`, { model_id: modelId })
          currentSession.value.model = modelId
          const session = sessions.value.find(s => s.id === currentSession.value.id)
          if (session) session.model = modelId
        } catch (e) {
          console.error('更新会话模型失败:', e)
        }
      }
      if (!selectedModel.value?.is_multimodal) {
        pendingImages.value = []
        pendingVideos.value = []
      }
    }

    const sendMessage = async () => {
      if (!canSend.value) return
      if (!currentSession.value) {
        await createNewSession()
        if (!currentSession.value) return
      }

      // 捕获当前会话 ID，防止异步期间会话切换导致串台
      const sessionId = currentSession.value.id
      const content = inputText.value.trim()
      const images = [...pendingImages.value]
      const videos = pendingVideos.value.map(v => v.url)
      sending.value = true
      inputText.value = ''
      pendingImages.value = []
      pendingVideos.value = []
      nextTick(() => {
        const el = inputRef.value
        if (el && !inputExpanded.value) el.style.height = 'auto'
      })

      messages.value.push({
        id: 'temp-' + Date.now(),
        role: 'user',
        content,
        images,
        videos,
        created_at: new Date().toISOString()
      })
      nextTick(() => scrollToBottom())

      try {
        const res = await api.post(`/assistant/sessions/${sessionId}/send/`, { content, images, videos }, { timeout: 300000 })
        // 如果用户在此期间切换了会话，不要把回复塞到新会话里
        if (currentSession.value?.id !== sessionId) return
        // 保留用户消息（替换 temp id 为真实 id），再追加助手回复
        messages.value = messages.value.map(m =>
          String(m.id).startsWith('temp-') ? { ...m, id: res.data.user_message?.id || m.id } : m
        )
        messages.value.push(res.data.message)
        const session = sessions.value.find(s => s.id === sessionId)
        if (session) {
          session.title = res.data.session.title
          session.last_message = { role: res.data.message.role, content: res.data.message.content.slice(0, 100) }
        }
        if (currentSession.value?.id === sessionId) {
          currentSession.value.title = res.data.session.title
        }
      } catch (e) {
        // 如果用户已切换会话，不弹错误提示
        if (currentSession.value?.id !== sessionId) return
        ElMessage.error(e.response?.data?.error || '发送失败')
      } finally {
        sending.value = false
        if (currentSession.value?.id === sessionId) {
          nextTick(() => scrollToBottom())
        }
      }
    }

    const scrollToBottom = () => {
      if (messagesRef.value) {
        messagesRef.value.scrollTop = messagesRef.value.scrollHeight
      }
    }

    // 附件上传（图片/视频）
    const triggerFilePick = (type) => {
      if (!selectedModel.value?.is_multimodal) {
        ElMessage.warning('当前模型不支持附件')
        return
      }
      filePickType.value = type
      fileInput.value.click()
    }

    const handleFileChange = async (e) => {
      const files = e.target.files
      if (!files || files.length === 0) return
      await uploadFiles(files, filePickType.value)
      e.target.value = ''
    }

    const handlePaste = async (e) => {
      if (!selectedModel.value?.is_multimodal) return
      const items = e.clipboardData?.items
      if (!items) return
      const imageFiles = []
      const videoFiles = []
      for (let item of items) {
        if (item.type.indexOf('image') !== -1) imageFiles.push(item.getAsFile())
        else if (item.type.indexOf('video') !== -1) videoFiles.push(item.getAsFile())
      }
      if (imageFiles.length > 0) {
        e.preventDefault()
        await uploadFiles(imageFiles, 'image')
      }
      if (videoFiles.length > 0) {
        e.preventDefault()
        await uploadFiles(videoFiles, 'video')
      }
    }

    const validateVideo = (file) => {
      return new Promise((resolve) => {
        if (file.size > 12 * 1024 * 1024) {
          ElMessage.error(`视频 ${(file.size / 1024 / 1024).toFixed(1)}MB，超过 12MB 限制`)
          resolve(false)
          return
        }
        const video = document.createElement('video')
        video.preload = 'metadata'
        const url = URL.createObjectURL(file)
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url)
          if (video.duration > 180) {
            ElMessage.error(`视频时长 ${Math.round(video.duration)} 秒，超过 180 秒限制`)
            resolve(false)
          } else resolve(true)
        }
        video.onerror = () => { URL.revokeObjectURL(url); resolve(false) }
        video.src = url
      })
    }

    const uploadFiles = async (files, type) => {
      if (!selectedModel.value?.is_multimodal) return
      uploadingImages.value = true
      try {
        const base64List = []
        for (let file of files) {
          if (type === 'video') {
            const ok = await validateVideo(file)
            if (!ok) continue
          }
          base64List.push(await fileToBase64(file))
        }
        if (base64List.length === 0) return
        const payload = type === 'video' ? { videos: base64List } : { images: base64List }
        const res = await api.post('/assistant/upload-images/', payload)
        if (type === 'video') {
          res.data.video_urls.forEach((url, i) => {
            pendingVideos.value.push({ url, localUrl: URL.createObjectURL(files[i]) })
          })
        } else {
          pendingImages.value.push(...res.data.urls)
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.error || (type === 'video' ? '视频上传失败' : '图片上传失败'))
      } finally {
        uploadingImages.value = false
      }
    }

    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    }

    const removePendingImage = (idx, type) => {
      if (type === 'video') {
        const item = pendingVideos.value[idx]
        if (item?.localUrl) URL.revokeObjectURL(item.localUrl)
        pendingVideos.value.splice(idx, 1)
      } else {
        pendingImages.value.splice(idx, 1)
      }
    }

    const renderMarkdown = (text) => {
      if (!text) return ''
      const html = marked.parse(text)
      return DOMPurify.sanitize(html)
    }

    // === 知识库面板 ===
    const knowledgePanelVisible = ref(false)
    const kbBases = ref([])
    const kbLoading = ref(false)
    const kbSearchKeyword = ref('')

    const filteredBases = computed(() => {
      const kw = kbSearchKeyword.value.trim().toLowerCase()
      if (!kw) return kbBases.value
      return kbBases.value.filter(b =>
        b.name?.toLowerCase().includes(kw) ||
        b.description?.toLowerCase().includes(kw)
      )
    })

    const openKnowledgePanel = async () => {
      knowledgePanelVisible.value = true
      kbSearchKeyword.value = ''
      kbLoading.value = true
      try {
        const res = await api.get('/knowledge/bases/')
        kbBases.value = res.data.results || res.data
      } catch (e) {
        ElMessage.error('获取知识库列表失败')
      } finally {
        kbLoading.value = false
      }
    }

    const importKnowledge = async (base) => {
      try {
        const res = await api.get(`/knowledge/bases/${base.id}/tree/`)
        const knowledges = res.data.knowledges || []
        let parts = []
        for (const k of knowledges) {
          for (const ch of (k.chapters || [])) {
            if (ch.content && ch.content.trim()) {
              // strip HTML tags to plain text
              const text = ch.content.replace(/<[^>]+>/g, '').trim()
              if (text) {
                parts.push(`【${k.name} / ${ch.name}】\n${text}`)
              }
            }
          }
        }
        if (parts.length === 0) {
          ElMessage.warning('该知识库没有内容')
          return
        }
        const knowledgeText = `以下是从知识库「${base.name}」导入的参考资料：\n\n${parts.join('\n\n')}\n\n请基于以上参考资料回答我的问题。`
        inputText.value = knowledgeText
        knowledgePanelVisible.value = false
        ElMessage.success(`已导入知识库「${base.name}」(${parts.length} 个章节)`)
      } catch (e) {
        ElMessage.error('导入知识库失败')
      }
    }

    watch(dialogVisible, (val) => {
      if (val) nextTick(() => inputRef.value?.focus())
    })

    onMounted(() => {
      initFloatPosition()
      window.addEventListener('resize', onResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', onResize)
      document.removeEventListener('mousemove', onDrag)
      document.removeEventListener('mouseup', stopDrag)
      document.removeEventListener('touchmove', onDrag)
      document.removeEventListener('touchend', stopDrag)
    })

    return {
      floatBtnRef,
      floatStyle,
      isDragging,
      isCollapsed,
      startDrag,
      handleFloatClick,
      dialogVisible,
      currentSession,
      sessions,
      sessionsLoading,
      messages,
      messagesRef,
      textModels,
      selectedModelId,
      selectedModel,
      inputText,
      inputRef,
      sending,
      inputExpanded,
      autoGrow,
      toggleInputExpand,
      pendingImages,
      pendingVideos,
      uploadingImages,
      fileInput,
      filePickType,
      canSend,
      createNewSession,
      selectSession,
      deleteSession,
      handleModelChange,
      sendMessage,
      triggerFilePick,
      handleFileChange,
      handlePaste,
      removePendingImage,
      renderMarkdown,
      Search,
      knowledgePanelVisible,
      kbBases,
      kbLoading,
      kbSearchKeyword,
      filteredBases,
      openKnowledgePanel,
      importKnowledge
    }
  }
}
</script>

<style scoped>
.ai-assistant-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 9999;
  pointer-events: none;
}

/* 悬浮按钮 */
.ai-float-btn {
  position: fixed;
  width: 80px;
  height: 64px;
  border-radius: 32px 32px 32px 12px;
  background: linear-gradient(135deg, #615ced 0%, #4f46e5 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(79, 70, 229, 0.32);
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.25s;
  pointer-events: auto;
  user-select: none;
  z-index: 9999;
}

.ai-float-btn:hover {
  transform: scale(1.04);
  box-shadow: 0 14px 36px rgba(79, 70, 229, 0.42);
}

.ai-float-btn.is-dragging {
  cursor: grabbing;
  transform: scale(1.08);
}

.ai-float-btn.is-collapsed {
  opacity: 0.75;
}

.ai-float-btn.is-collapsed:hover {
  opacity: 1;
}

/* 弹窗打开时悬浮按钮保持在遮罩之上，可再次点击关闭 */
.ai-float-btn.is-open {
  z-index: 10000;
  opacity: 0.92;
  background: linear-gradient(135deg, #4338ca 0%, #3730a3 100%);
}

.float-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.float-label {
  font-size: 11px;
  font-weight: 600;
}

/* 千问风格弹窗 */
:global(.ai-assistant-dialog.qwen-style) {
  border-radius: 16px !important;
  overflow: hidden !important;
}

:global(.ai-assistant-dialog.qwen-style .el-dialog__header) {
  display: none !important;
}

:global(.ai-assistant-dialog.qwen-style .el-dialog__body) {
  padding: 0 !important;
}

.qwen-layout {
  display: flex;
  height: 88vh;
  background: #fff;
}

/* 左侧边栏 */
.qwen-sidebar {
  width: 260px;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 16px;
}

.new-chat-btn {
  width: 100%;
  height: 40px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #1f2937;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.new-chat-btn:hover {
  border-color: #615ced;
  color: #615ced;
  background: #f5f3ff;
}

.sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 16px;
}

.sidebar-section-title {
  font-size: 12px;
  color: #9ca3af;
  padding: 12px 8px 8px;
  font-weight: 500;
}

.session-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.session-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
  color: #4b5563;
  position: relative;
}

.session-item:hover {
  background: #f3f4f6;
}

.session-item.active {
  background: #ede9fe;
  color: #4f46e5;
}

.session-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.session-title {
  flex: 1;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-delete {
  opacity: 0;
  font-size: 14px;
  color: #9ca3af;
  transition: opacity 0.2s, color 0.2s;
  padding: 2px;
}

.session-item:hover .session-delete {
  opacity: 1;
}

.session-delete:hover {
  color: #ef4444;
}

.empty-sessions {
  padding: 24px 8px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}

/* 主区域 */
.qwen-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #fff;
}

.qwen-header {
  height: 60px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 20px;
  gap: 12px;
}

.header-spacer {
  flex: 1;
}

.model-selector {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  transition: background 0.2s;
}

.model-selector:hover {
  background: #f3f4f6;
}

.model-arrow {
  font-size: 12px;
  color: #9ca3af;
}

.model-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 140px;
}

:global(.ai-assistant-dialog.qwen-style .el-dropdown-menu__item.active) {
  color: #4f46e5;
  background: #f5f3ff;
}

.close-btn {
  color: #6b7280;
}

/* 内容区 */
.qwen-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px 20%;
  background: #fff;
}

.qwen-welcome {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding-bottom: 120px;
}

.welcome-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;
}

.welcome-logo {
  font-size: 36px;
}

.welcome-tips {
  font-size: 14px;
  color: #9ca3af;
}

.message-row {
  display: flex;
  margin-bottom: 24px;
}

.message-row.user {
  justify-content: flex-end;
}

.message-row.assistant {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 80%;
  padding: 14px 18px;
  border-radius: 18px;
  font-size: 15px;
  line-height: 1.7;
  color: #1f2937;
  word-break: break-word;
}

.message-row.user .message-bubble {
  background: #ede9fe;
  color: #1f2937;
  border-bottom-right-radius: 6px;
}

.message-row.assistant .message-bubble {
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-bottom-left-radius: 6px;
}

.message-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.message-image {
  width: 88px;
  height: 88px;
  border-radius: 10px;
  object-fit: cover;
  cursor: pointer;
  border: 1px solid #e5e7eb;
}

.message-videos {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.message-video {
  width: 320px;
  max-width: 100%;
  border-radius: 12px;
  background: #000;
  border: 1px solid #e5e7eb;
}

.pending-video-item .pending-video {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
  background: #000;
}

.message-text :deep(pre) {
  background: #1e1e2e;
  color: #e4e4e7;
  padding: 14px;
  border-radius: 10px;
  overflow-x: auto;
  margin: 10px 0;
  font-size: 13px;
}

.message-text :deep(code) {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
}

.message-text :deep(pre code) {
  background: none;
  padding: 0;
  border-radius: 0;
  color: inherit;
  font-size: inherit;
}

.message-text :deep(h1) {
  font-size: 1.5em;
  font-weight: 700;
  margin: 16px 0 8px;
}

.message-text :deep(h2) {
  font-size: 1.3em;
  font-weight: 700;
  margin: 14px 0 6px;
}

.message-text :deep(h3) {
  font-size: 1.15em;
  font-weight: 600;
  margin: 12px 0 6px;
}

.message-text :deep(p) {
  margin: 6px 0;
}

.message-text :deep(ul),
.message-text :deep(ol) {
  padding-left: 1.5em;
  margin: 6px 0;
}

.message-text :deep(li) {
  margin: 4px 0;
}

.message-text :deep(blockquote) {
  border-left: 3px solid #c0c0c0;
  padding-left: 12px;
  color: #6b7280;
  margin: 8px 0;
}

.message-text :deep(a) {
  color: #4f46e5;
  text-decoration: underline;
}

.message-text :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}

.message-text :deep(th),
.message-text :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 6px 10px;
  font-size: 14px;
}

.message-text :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 12px 0;
}

.typing-indicator {
  display: flex;
  gap: 5px;
  padding: 6px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #d1d5db;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* 底部输入区 */
.qwen-footer {
  padding: 0 20% 28px;
  background: #fff;
}

.input-card {
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 12px 14px;
  background: #fff;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-card:focus-within {
  border-color: #615ced;
  box-shadow: 0 4px 28px rgba(79, 70, 229, 0.1);
}

.quick-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.quick-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 8px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-tag:hover {
  background: #ede9fe;
  color: #4f46e5;
}

/* 知识库面板 */
.kb-panel {
  max-height: 60vh;
  display: flex;
  flex-direction: column;
}

.kb-search {
  margin-bottom: 8px;
}

.kb-sort-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 8px;
  padding-left: 2px;
}

.kb-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.kb-pick-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
}

.kb-pick-item:hover {
  border-color: #6366f1;
  background: #f5f3ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 12px rgba(99, 102, 241, 0.1);
}

.kb-pick-icon {
  font-size: 22px;
  color: #6366f1;
  flex-shrink: 0;
}

.kb-pick-body {
  flex: 1;
  min-width: 0;
}

.kb-pick-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}

.kb-pick-desc {
  font-size: 12px;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kb-pick-count {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 6px;
  flex-shrink: 0;
}

.pending-images {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.pending-image-item {
  position: relative;
  width: 56px;
  height: 56px;
}

.pending-image {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
}

.pending-image-delete {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 10px;
}

.input-line {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  transition: align-items 0.2s;
}

.input-line.expanded {
  align-items: flex-end;
}

.attach-btn {
  color: #9ca3af;
  flex-shrink: 0;
  margin-bottom: 2px;
}

.attach-btn:hover {
  color: #615ced;
  background: #f5f3ff;
}

.qwen-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  line-height: 24px;
  color: #1f2937;
  background: transparent;
  padding: 6px 0;
  resize: none;
  font-family: inherit;
  min-height: 48px;
  max-height: none;
  overflow-y: auto;
  scrollbar-width: thin;
}

.qwen-input::placeholder {
  color: #9ca3af;
}

.input-side-btns {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.expand-btn {
  color: #9ca3af;
}

.expand-btn:hover {
  color: #615ced;
  background: #f5f3ff;
}

.send-btn {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  background: #e5e7eb;
  color: #9ca3af;
  border: none;
  transition: all 0.2s;
}

.send-btn.active {
  background: #4f46e5;
  color: #fff;
}

.send-btn.active:hover {
  background: #4338ca;
}

@media (max-width: 768px) {
  .qwen-sidebar {
    display: none;
  }
  .qwen-content,
  .qwen-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
  .message-bubble {
    max-width: 92%;
  }
}
</style>
